import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "sw";

const en = {
  appName: "Smart Safiri Agents",
  switchLanguage: "Kiswahili",
  loading: "Loading…",
  retry: "Try again",
  save: "Save",
  saved: "Saved",
  cancel: "Cancel",
  close: "Close",
  continue: "Continue",
  back: "Back",
  optional: "optional",
  required: "Required",
  somethingWrong: "Something went wrong. Please try again.",

  // Auth
  signIn: "Sign in",
  signOut: "Sign out",
  phone: "Phone number",
  phoneHint: "Tanzanian mobile, e.g. +255712345678",
  password: "Password",
  passwordHint: "At least 8 characters, with a letter and a number",
  forgotPassword: "Forgot password?",
  noAccountYet: "Not an agent yet?",
  applyNow: "Apply to become an agent",
  haveAccount: "Already an agent?",
  welcomeTitle: "Earn by bringing drivers to Safiri",
  welcomeBody: "Share your code. Each new driver you bring can earn you up to TZS 10,000 as they get approved and start driving.",

  applyTitle: "Apply to become an agent",
  fullName: "Full name, as on your ID",
  email: "Email",
  language: "Preferred language",
  english: "English",
  swahili: "Swahili",
  sendCode: "Send SMS code",
  resendCode: "Send a new code",
  smsCode: "Verification code",
  smsCodeSent: "We sent a code to {phone}.",
  createAccount: "Create account",

  verifyEmailTitle: "Verify your email",
  emailVerified: "Your email is verified. You can close this page or continue.",
  verifying: "Verifying…",
  resendVerification: "Send the verification email again",
  verificationSent: "Verification email sent. Check your inbox.",

  forgotTitle: "Reset your password",
  forgotBody: "Enter your phone number or email. If there's an agent account, we'll email a reset link.",
  phoneOrEmail: "Phone number or email",
  sendLink: "Send reset link",
  linkSent: "If an account exists, a reset link is on its way to the account's email.",
  newPassword: "New password",
  setPassword: "Set password",
  passwordReset: "Password changed. Sign in with your new password.",

  inviteTitle: "Set up your agent account",
  inviteBody: "Hi {name}. Safiri created an agent account for you. Verify your phone {phone}, set a password and accept the terms to start.",
  acceptTerms: "I accept the agent terms and conditions",
  acceptConsent: "I agree to Safiri processing my personal data under the Personal Data Protection Act, 2022",
  readTerms: "Read the terms",
  finishSetup: "Finish setup",

  // Application
  applicationTitle: "Your application",
  statusDraft: "Finish your profile and submit it for review.",
  statusUnderReview: "Your application is under review. We'll let you know by SMS and email.",
  statusRejected: "Your application wasn't approved. Fix the points below and submit again.",
  statusSuspended: "Your account is suspended. Your code doesn't work for new drivers and cash-out is paused. Contact Safiri support.",
  rejectedFor: "Reasons",
  personal: "Personal details",
  dob: "Date of birth",
  gender: "Gender",
  male: "Male",
  female: "Female",
  location: "Where you live",
  region: "Region",
  district: "District",
  street: "Ward / street",
  identity: "Identity",
  idDocument: "ID document",
  idDocumentHint: "One clear photo of a government ID with your name, photo and date of birth (NIDA, passport, driving licence or voter ID). JPG, PNG or PDF, up to 5 MB.",
  profilePhoto: "Profile photo",
  profilePhotoHint: "A clear photo of your face. JPG or PNG, up to 5 MB.",
  upload: "Upload",
  replace: "Replace",
  uploaded: "Uploaded",
  more: "More about you",
  altPhone: "Alternative phone number",
  heardFrom: "How did you hear about Safiri?",
  heardSafiriStaff: "Safiri staff",
  heardAnotherAgent: "Another agent",
  heardSocialMedia: "Social media",
  heardOther: "Other",
  referringStaff: "Referring Safiri staff member",
  stillMissing: "Still needed before you can submit",
  miss_name: "Full name",
  miss_emailVerified: "Verified email (open the link we emailed you)",
  miss_phoneVerified: "Verified phone",
  miss_dob: "Date of birth",
  miss_gender: "Gender",
  miss_region: "Region",
  miss_district: "District",
  miss_street: "Ward / street",
  miss_idDocument: "ID document",
  miss_profilePhoto: "Profile photo",
  emailNotVerified: "Email not verified yet: {email}",
  submit: "Submit for approval",
  submitted: "Submitted. We'll review your application soon.",
  deleteApplication: "Delete application",

  // App
  navHome: "Home",
  navCode: "My code",
  navDrivers: "Drivers",
  navWallet: "Wallet",
  navMore: "More",
  navNotifications: "Notifications",
  navProfile: "Profile",
  navHelp: "Help",

  hello: "Hello, {name}",
  driversReferred: "Drivers referred",
  rewardsCredited: "Rewards credited",
  walletBalance: "Wallet balance",
  stillPossible: "Still possible",
  stillPossibleHint: "From referrals in progress, if they reach every milestone in time",
  byStatus: "Rewards by status",

  myCode: "Your referral code",
  codeHint: "New drivers type this code when they sign up in the Safiri driver app.",
  copy: "Copy",
  copied: "Copied",
  shareWhatsApp: "Share on WhatsApp",
  shareSms: "Share by SMS",
  showQr: "QR code",
  printQr: "Print",
  shareMessage: "Drive with Smart Safiri! Download the driver app: {link} and enter my referral code {code} when you sign up.",

  searchDrivers: "Search by name or phone",
  allDriverStatuses: "All driver statuses",
  allRewardStatuses: "All reward statuses",
  noReferrals: "No drivers yet. Share your code to get started.",
  trips: "{n} trips",
  lastTrip: "Last trip {date}",
  paidOf: "{paid} of {max}",

  ds_documentsPending: "Documents pending",
  ds_underReview: "Under review",
  ds_active: "Active",
  ds_rejected: "Rejected",
  ds_documentsExpired: "Documents expired",
  ds_suspended: "Suspended",
  ds_deleted: "Deleted",

  rs_pending: "Pending",
  rs_inProgress: "In progress",
  rs_completed: "Completed",
  rs_closed: "Closed",

  ts_locked: "Not reached yet",
  ts_paid: "Paid",
  ts_expired: "Expired",
  ts_cancelled: "Cancelled",
  ts_reversed: "Reversed",

  referralDetail: "Referral",
  rewardProgress: "Reward progress",
  milestone: "Milestone",
  deadline: "Deadline",
  daysLeft: "{n} days left",
  startsAfter: "{days} days from {anchor}",
  anchor_signup: "sign-up",
  anchor_activation: "activation",
  anchor_firstTrip: "the first trip",
  paidOn: "Paid {date}",
  signedUp: "Signed up {date}",
  callDriver: "Call",

  wallet: "Wallet",
  available: "Available to cash out",
  minimum: "Minimum cash-out: {amount}",
  cashOut: "Cash out {amount}",
  cashOutTitle: "Cash out",
  cashOutFull: "You cash out your full balance. Safiri pays the transfer fee.",
  payTo: "Pay to",
  addAccount: "Add a new account",
  provider: "Bank or mobile money",
  accountNumber: "Account or phone number",
  accountName: "Name on the account",
  nameMustMatch: "The account must be in your name.",
  confirmCashOut: "Send {amount}",
  cashOutSent: "Cash-out on its way: {amount}",
  history: "History",
  noHistory: "No wallet activity yet.",
  h_reward: "Referral reward",
  h_reversal: "Reward reversed",
  h_cashout: "Cash-out",
  h_forfeit: "Balance forfeited",
  co_PENDING: "Pending",
  co_PROCESSING: "On its way",
  co_COMPLETED: "Sent",
  co_FAILED: "Failed, balance restored",

  notifications: "Notifications",
  noNotifications: "Nothing yet.",
  markAllRead: "Mark all as read",

  profile: "Profile",
  lockedFields: "Name, date of birth and ID can only be changed by Safiri support.",
  changePassword: "Change password",
  currentPassword: "Current password",
  passwordChanged: "Password changed. Other devices are signed out.",
  changePhone: "Change phone number",
  changePhoneBody: "We'll send a code to your current number and to the new one. Lost your old number? Contact Safiri support.",
  newPhone: "New phone number",
  codeToOld: "Code sent to your current number",
  codeToNew: "Code sent to the new number",
  phoneChanged: "Phone number changed.",
  emailChangeSent: "We sent a link to {email}. Your email changes once you open it.",
  deleteAccount: "Delete account",
  deleteBody: "Your code stops working and all your open referrals close. This can't be undone, and you can't apply again with the same phone number.",
  forfeitWarning: "Your balance of {amount} is below the minimum cash-out and will be lost.",
  cashOutFirst: "Cash out your balance before deleting your account.",
  delUnderReview: "You can't delete your account while your application is under review.",
  delPayout: "Wait for your cash-out to finish before deleting your account.",
  delNegative: "Your wallet balance is negative. Contact Safiri support to delete your account.",
  confirmDelete: "Delete my account",
  confirmWithPassword: "Enter your password to confirm",

  help: "Help",
  contactSupport: "Contact Safiri support",
  terms: "Agent terms and conditions",

  // Polish + PWA
  agentsLabel: "AGENTS",
  mainNav: "Main menu",
  all: "All",
  add: "Add",
  view: "View",
  clear: "Clear",
  refresh: "Refresh",
  choose: "Choose…",
  next: "Next",
  previous: "Previous",
  pagination: "Pages",
  pageOf: "Page {page} of {pages}",
  loadMore: "Load more",
  seeAll: "See all",
  gotIt: "Got it",
  verified: "Verified",
  notVerified: "Not verified yet. Check your inbox for the link.",
  unread: "Unread",
  showPassword: "Show password",
  hidePassword: "Hide password",
  steps: "Steps",
  madeInTz: "Made for Tanzania",
  goHome: "Go home",
  notFoundTitle: "Page not found",
  notFoundBody: "The link may be old or mistyped.",
  offlineBanner: "You're offline. Showing what you last saw.",
  updateReady: "A new version of the app is ready.",
  updateNow: "Update",
  brandHeadline: "Bring drivers to Smart Safiri. Get paid for every milestone.",
  brandPoint1: "Share your code with new drivers",
  brandPoint2: "They sign up, get approved and start driving",
  brandPoint3: "Rewards land in your wallet. Cash out to mobile money or bank",
  upToPerDriver: "Up to TZS 10,000 for every new driver you bring.",
  signInTitle: "Welcome back",
  signInBody: "Sign in with your phone number and password.",
  applyPitch: "Apply in a few minutes. Once approved you get your own referral code.",
  minCashoutNote: "Cash out from {amount}.",
  applyBody: "Create your account first. You'll add your details and ID next.",
  stepDetails: "Your details",
  stepVerifyPhone: "Verify phone",
  fullNameHint: "Exactly as it appears on your ID.",
  phoneVerifyHint: "We'll send a code on WhatsApp (or SMS) to check it's yours.",
  languageHint: "Used for SMS and email from Safiri.",
  errName: "Enter your full name.",
  errPhone: "Enter a Tanzanian mobile number.",
  errEmail: "Enter a valid email address.",
  pwRuleLength: "8+ characters",
  pwRuleLetter: "A letter",
  pwRuleNumber: "A number",
  editDetails: "Edit details",
  resendIn: "Resend in {s}s",
  verifyEmailFailed: "We couldn't verify your email",
  checkEmail: "Check your email",
  backToSignIn: "Back to sign in",
  resetTitle: "Choose a new password",
  resetBody: "You'll be signed out on every device.",
  confirmPassword: "Confirm new password",
  passwordsDiffer: "The passwords don't match.",
  passwordUpdated: "Password updated",
  inviteInvalid: "This invite link doesn't work",
  profileProgress: "Profile {done} of {total} complete",
  verifyYourEmail: "Verify your email",
  reviewTitle: "Thanks! We're checking your details.",
  submittedOn: "Submitted {date}",
  dobHint: "You must be 18 or older.",
  pickRegionFirst: "Pick a region first",
  tapToUpload: "Tap to upload",
  uploadDone: "Uploaded",
  openFile: "Open file",
  fileTooBig: "That file is over 5 MB. Choose a smaller one.",
  referringStaffHint: "For our records only.",
  agreeAndSubmit: "Agree and submit",
  as_draft: "Draft",
  as_underReview: "Under review",
  as_rejected: "Not approved",
  as_approved: "Approved agent",
  as_suspended: "Suspended",
  suspendedTitle: "Account suspended",
  shareCode: "Share code",
  shareTitle: "Join Smart Safiri as a driver",
  codeCopied: "Code copied",
  messageCopied: "Message copied",
  copyMessage: "Copy message",
  codePageBody: "Print it, share it, or show it on your phone.",
  qrTitle: "Scan for my referral code",
  downloadQr: "Download",
  shareMessageTitle: "Ready-made message",
  howItWorks: "How it works",
  how1: "The driver downloads the Smart Safiri driver app.",
  how2: "They type your code when they sign up.",
  how3: "You earn as they're approved and complete trips, within each time limit.",
  driverPipeline: "Your drivers",
  recentDrivers: "Recent drivers",
  firstDriverTitle: "No drivers yet",
  firstDriverBody: "Share your code with someone who wants to drive. You'll see them here once they sign up.",
  driversCount: "{n} drivers referred",
  driverStatus: "Driver status",
  searchPlaceholder: "Name or phone",
  noMatches: "No drivers match",
  noMatchesBody: "Try another name or clear the filters.",
  clearFilters: "Clear filters",
  signedUpLabel: "Signed up",
  activatedLabel: "Approved",
  firstTripLabel: "First trip",
  lastTripLabel: "Last trip",
  driverDeletedNote: "This driver deleted their account. Rewards already paid stay yours.",
  stillToEarn: "Up to {amount} more if the next milestones are reached in time.",
  milestones: "Milestones",
  toMinimum: "{amount} more to reach the {min} minimum",
  payoutAccounts: "Payout accounts",
  payoutAccountsBody: "Where your cash-outs go. Must be in your name.",
  noAccounts: "No accounts yet. Add one now or at your first cash-out.",
  addAccountBody: "Mobile money or bank, in your own name.",
  saveAccount: "Save account",
  accountSaved: "Account saved",
  removeAccount: "Remove",
  removeAccountTitle: "Remove this account?",
  removeAccountBody: "You can add it again later. Past cash-outs aren't affected.",
  accountRemoved: "Account removed",
  mobileMoney: "Mobile money",
  bank: "Bank",
  bankAccountNumber: "Bank account number",
  mobileNumberHint: "The phone number on the wallet, e.g. 0712345678.",
  verifiedAccount: "Account holder confirmed",
  nameMismatch: "Name differs from yours",
  nameMismatchWarn: "This account's name doesn't match yours. Safiri may check it before more payouts.",
  chooseAccount: "Choose where to send your money.",
  useSavedAccount: "Use a saved account",
  confirmTitle: "Confirm cash-out",
  youWillReceive: "You'll receive",
  noFee: "No transfer fee: Safiri pays it",
  filterRewards: "Rewards",
  filterCashouts: "Cash-outs",
  filterOther: "Reversals",
  noHistoryBody: "Rewards appear here as your drivers reach milestones.",
  receiptRef: "Ref",
  unreadCount: "{n} unread",
  unreadNotifications: "Notifications, {n} unread",
  allRead: "All marked as read",
  noNotificationsBody: "We'll let you know when drivers sign up, milestones are paid and cash-outs land.",
  pushTitle: "Push notifications",
  pushOn: "Notifications are on",
  pushOnBody: "You'll get alerts on this device.",
  pushOffTitle: "Get instant alerts",
  pushOffBody: "Know the moment a driver signs up or you get paid.",
  pushDenied: "Notifications are blocked for this site. Allow them in your browser settings.",
  pushUnsupported: "This browser can't show notifications. You'll still get SMS and email.",
  pushUnavailable: "Push notifications aren't available yet. You'll still get SMS and email.",
  pushIosInstall: "On iPhone, add the app to your home screen to turn on notifications.",
  pushTurnedOn: "Notifications turned on",
  pushTurnedOff: "Notifications turned off",
  turnOn: "Turn on",
  turnOff: "Turn off",
  install: "Install",
  installTitle: "Install the agent app",
  installShort: "Quick access from your home screen",
  installBody: "Add Smart Safiri Agents to your home screen. It opens like an app and works with a weak connection.",
  iosStep1: "Tap the Share button in Safari",
  iosStep2: "Choose \"Add to Home Screen\"",
  iosStep3: "Tap Add. The app appears on your home screen",
  photoUpdated: "Profile photo updated",
  agentSince: "Agent since {date}",
  contactDetails: "Contact details",
  editAfterReview: "You can edit these once your application has been reviewed.",
  saveChanges: "Save changes",
  preferences: "Preferences",
  appearance: "Appearance",
  themeSystem: "Auto",
  themeLight: "Light",
  themeDark: "Dark",
  themeToggle: "Theme: {mode}. Tap to change",
  security: "Security",
  changePasswordBody: "Other devices will be signed out.",
  currentPhone: "Current number",
  dangerZone: "Delete",
  deleteShort: "Permanently close your agent account",
  deleteUnderstand: "I understand this can't be undone.",
  goToWallet: "Go to wallet",
  helpBody: "We're here to help, in English or Swahili.",
  callUs: "Call us",
  chatWithUs: "Chat with us",
  emailUs: "Email us",
  faqTitle: "Common questions",
  faq1q: "How do I earn?",
  faq1a: "Share your code. When a new driver signs up with it, you earn in steps: when Safiri approves them, at their first completed trip, and at their fifth. Each step has its own time limit, shown on the driver's page.",
  faq2q: "When is a reward paid?",
  faq2a: "Straight away, the moment a milestone is reached inside its time limit. It appears in your wallet and you get a notification.",
  faq3q: "Why did a milestone expire?",
  faq3a: "Each milestone must be reached before 23:59 Tanzania time on its last day. If it's missed, only that milestone expires; later ones stay open. Calling your driver to remind them helps.",
  faq4q: "How do I cash out?",
  faq4a: "Open Wallet and tap Cash out once your balance reaches {min}. You cash out the full balance to mobile money or a bank account in your name. Safiri pays the fee.",
  faq5q: "Why can't I cash out?",
  faq5a: "Cash-out is paused while a previous one is still on its way, while your balance is below the minimum or negative, or while your account is suspended. The Wallet page tells you which.",
  faq6q: "Can I change my name or ID?",
  faq6a: "Name, date of birth and ID can only be changed by Safiri support after checking your identity. Contact us using the options above.",
  faq7q: "A driver says my code doesn't work",
  faq7a: "Check they typed it exactly, letters and numbers. Codes only work for new drivers: someone who had a Safiri driver account before can't be referred. If your account is suspended, your code is paused.",
  account: "Account",
  support: "Support",
  profileShort: "Contact details, password, language",
  helpShort: "Support contacts and answers",
  emailOptionalHint: "Optional. We'll send receipts and updates here if you add one.",
  forgotSmsBody: "We'll send a code to the phone number on your account, on WhatsApp or by SMS.",
  forgotEmailBody: "Enter the email on your account and we'll send a reset link.",
  useEmailInstead: "Use my email instead",
  useSmsInstead: "Get the code by SMS instead",
  changeNumber: "Change number",
  verifyNotNeededTitle: "No email check needed",
  verifyNotNeededBody: "Agents don't need to verify their email any more. You can carry on.",
  sendCodeWhatsApp: "Send code on WhatsApp",
  sendCodeSms: "Send code by SMS",
  useWhatsAppInstead: "Get the code on WhatsApp instead",
  codeSentWhatsApp: "We sent a code to {phone} on WhatsApp.",
  sendBySmsInstead: "Send by SMS instead",
  sendOnWhatsAppInstead: "Send on WhatsApp instead",
  codesSentWhatsApp: "We sent a code on WhatsApp to each number.",
  codesSentSms: "We sent a code by SMS to each number.",
  usePhoneCodeInstead: "Use a code to my phone instead",
} as const;

type Key = keyof typeof en;

const sw: Record<Key, string> = {
  appName: "Mawakala wa Smart Safiri",
  switchLanguage: "English",
  loading: "Inapakia…",
  retry: "Jaribu tena",
  save: "Hifadhi",
  saved: "Imehifadhiwa",
  cancel: "Ghairi",
  close: "Funga",
  continue: "Endelea",
  back: "Rudi",
  optional: "si lazima",
  required: "Inahitajika",
  somethingWrong: "Kuna tatizo. Tafadhali jaribu tena.",

  signIn: "Ingia",
  signOut: "Toka",
  phone: "Namba ya simu",
  phoneHint: "Namba ya Tanzania, mfano +255712345678",
  password: "Nenosiri",
  passwordHint: "Angalau herufi 8, zenye herufi na namba",
  forgotPassword: "Umesahau nenosiri?",
  noAccountYet: "Bado si wakala?",
  applyNow: "Omba kuwa wakala",
  haveAccount: "Tayari ni wakala?",
  welcomeTitle: "Pata kipato kwa kuleta madereva Safiri",
  welcomeBody: "Shiriki msimbo wako. Kila dereva mpya unayemleta anaweza kukupatia hadi TZS 10,000 anapoidhinishwa na kuanza kuendesha.",

  applyTitle: "Omba kuwa wakala",
  fullName: "Jina kamili, kama kwenye kitambulisho",
  email: "Barua pepe",
  language: "Lugha unayopendelea",
  english: "Kiingereza",
  swahili: "Kiswahili",
  sendCode: "Tuma msimbo wa SMS",
  resendCode: "Tuma msimbo mpya",
  smsCode: "Msimbo wa uthibitisho",
  smsCodeSent: "Tumetuma msimbo kwa {phone}.",
  createAccount: "Fungua akaunti",

  verifyEmailTitle: "Thibitisha barua pepe yako",
  emailVerified: "Barua pepe yako imethibitishwa. Unaweza kufunga ukurasa huu au kuendelea.",
  verifying: "Inathibitisha…",
  resendVerification: "Tuma tena barua pepe ya uthibitisho",
  verificationSent: "Barua pepe ya uthibitisho imetumwa. Angalia kisanduku chako.",

  forgotTitle: "Badilisha nenosiri",
  forgotBody: "Weka namba ya simu au barua pepe. Ikiwa kuna akaunti ya wakala, tutatuma kiungo cha kubadilisha nenosiri.",
  phoneOrEmail: "Namba ya simu au barua pepe",
  sendLink: "Tuma kiungo",
  linkSent: "Ikiwa akaunti ipo, kiungo kinatumwa kwenye barua pepe ya akaunti.",
  newPassword: "Nenosiri jipya",
  setPassword: "Weka nenosiri",
  passwordReset: "Nenosiri limebadilishwa. Ingia kwa nenosiri jipya.",

  inviteTitle: "Kamilisha akaunti yako ya wakala",
  inviteBody: "Habari {name}. Safiri imekufungulia akaunti ya wakala. Thibitisha simu yako {phone}, weka nenosiri na ukubali masharti uanze.",
  acceptTerms: "Nakubali masharti na vigezo vya wakala",
  acceptConsent: "Nakubali Safiri kuchakata taarifa zangu binafsi chini ya Sheria ya Ulinzi wa Taarifa Binafsi, 2022",
  readTerms: "Soma masharti",
  finishSetup: "Kamilisha",

  applicationTitle: "Maombi yako",
  statusDraft: "Kamilisha wasifu wako na uutume ukaguliwe.",
  statusUnderReview: "Maombi yako yanapitiwa. Tutakujulisha kwa SMS na barua pepe.",
  statusRejected: "Maombi yako hayakuidhinishwa. Rekebisha mambo yaliyo hapa chini na utume tena.",
  statusSuspended: "Akaunti yako imesimamishwa. Msimbo wako hautumiki kwa madereva wapya na kutoa pesa kumesitishwa. Wasiliana na Safiri.",
  rejectedFor: "Sababu",
  personal: "Taarifa binafsi",
  dob: "Tarehe ya kuzaliwa",
  gender: "Jinsia",
  male: "Mwanaume",
  female: "Mwanamke",
  location: "Unapoishi",
  region: "Mkoa",
  district: "Wilaya",
  street: "Kata / mtaa",
  identity: "Utambulisho",
  idDocument: "Kitambulisho",
  idDocumentHint: "Picha moja safi ya kitambulisho cha serikali chenye jina, picha na tarehe ya kuzaliwa (NIDA, pasipoti, leseni ya udereva au kadi ya mpiga kura). JPG, PNG au PDF, hadi MB 5.",
  profilePhoto: "Picha yako",
  profilePhotoHint: "Picha safi ya uso wako. JPG au PNG, hadi MB 5.",
  upload: "Pakia",
  replace: "Badilisha",
  uploaded: "Imepakiwa",
  more: "Zaidi kukuhusu",
  altPhone: "Namba nyingine ya simu",
  heardFrom: "Ulisikia kuhusu Safiri wapi?",
  heardSafiriStaff: "Mfanyakazi wa Safiri",
  heardAnotherAgent: "Wakala mwingine",
  heardSocialMedia: "Mitandao ya kijamii",
  heardOther: "Nyingine",
  referringStaff: "Mfanyakazi wa Safiri aliyekuleta",
  stillMissing: "Bado vinahitajika kabla ya kutuma",
  miss_name: "Jina kamili",
  miss_emailVerified: "Barua pepe iliyothibitishwa (fungua kiungo tulichokutumia)",
  miss_phoneVerified: "Simu iliyothibitishwa",
  miss_dob: "Tarehe ya kuzaliwa",
  miss_gender: "Jinsia",
  miss_region: "Mkoa",
  miss_district: "Wilaya",
  miss_street: "Kata / mtaa",
  miss_idDocument: "Kitambulisho",
  miss_profilePhoto: "Picha yako",
  emailNotVerified: "Barua pepe bado haijathibitishwa: {email}",
  submit: "Tuma ili kuidhinishwa",
  submitted: "Imetumwa. Tutapitia maombi yako hivi karibuni.",
  deleteApplication: "Futa maombi",

  navHome: "Nyumbani",
  navCode: "Msimbo",
  navDrivers: "Madereva",
  navWallet: "Mkoba",
  navMore: "Zaidi",
  navNotifications: "Taarifa",
  navProfile: "Wasifu",
  navHelp: "Msaada",

  hello: "Habari, {name}",
  driversReferred: "Madereva uliowaleta",
  rewardsCredited: "Zawadi zilizolipwa",
  walletBalance: "Salio la mkoba",
  stillPossible: "Bado zinawezekana",
  stillPossibleHint: "Kutoka rufaa zinazoendelea, wakifikia kila hatua kwa wakati",
  byStatus: "Zawadi kwa hali",

  myCode: "Msimbo wako wa rufaa",
  codeHint: "Madereva wapya huandika msimbo huu wanapojisajili kwenye programu ya madereva ya Safiri.",
  copy: "Nakili",
  copied: "Imenakiliwa",
  shareWhatsApp: "Shiriki WhatsApp",
  shareSms: "Shiriki kwa SMS",
  showQr: "Msimbo QR",
  printQr: "Chapisha",
  shareMessage: "Endesha na Smart Safiri! Pakua programu ya madereva: {link} na uweke msimbo wangu wa rufaa {code} unapojisajili.",

  searchDrivers: "Tafuta kwa jina au simu",
  allDriverStatuses: "Hali zote za dereva",
  allRewardStatuses: "Hali zote za zawadi",
  noReferrals: "Bado hakuna madereva. Shiriki msimbo wako kuanza.",
  trips: "Safari {n}",
  lastTrip: "Safari ya mwisho {date}",
  paidOf: "{paid} kati ya {max}",

  ds_documentsPending: "Hati zinasubiriwa",
  ds_underReview: "Inapitiwa",
  ds_active: "Hai",
  ds_rejected: "Imekataliwa",
  ds_documentsExpired: "Hati zimeisha muda",
  ds_suspended: "Imesimamishwa",
  ds_deleted: "Imefutwa",

  rs_pending: "Inasubiri",
  rs_inProgress: "Inaendelea",
  rs_completed: "Imekamilika",
  rs_closed: "Imefungwa",

  ts_locked: "Bado haijafikiwa",
  ts_paid: "Imelipwa",
  ts_expired: "Muda umeisha",
  ts_cancelled: "Imefutwa",
  ts_reversed: "Imeondolewa",

  referralDetail: "Rufaa",
  rewardProgress: "Maendeleo ya zawadi",
  milestone: "Hatua",
  deadline: "Mwisho",
  daysLeft: "Siku {n} zimebaki",
  startsAfter: "Siku {days} baada ya {anchor}",
  anchor_signup: "kujisajili",
  anchor_activation: "kuidhinishwa",
  anchor_firstTrip: "safari ya kwanza",
  paidOn: "Imelipwa {date}",
  signedUp: "Alijisajili {date}",
  callDriver: "Piga",

  wallet: "Mkoba",
  available: "Unaweza kutoa",
  minimum: "Kiwango cha chini cha kutoa: {amount}",
  cashOut: "Toa {amount}",
  cashOutTitle: "Toa pesa",
  cashOutFull: "Unatoa salio lako lote. Safiri inalipa ada ya kutuma.",
  payTo: "Lipa kwa",
  addAccount: "Ongeza akaunti mpya",
  provider: "Benki au huduma ya pesa kwa simu",
  accountNumber: "Namba ya akaunti au simu",
  accountName: "Jina kwenye akaunti",
  nameMustMatch: "Akaunti lazima iwe kwa jina lako.",
  confirmCashOut: "Tuma {amount}",
  cashOutSent: "Malipo yanatumwa: {amount}",
  history: "Historia",
  noHistory: "Bado hakuna shughuli kwenye mkoba.",
  h_reward: "Zawadi ya rufaa",
  h_reversal: "Zawadi imeondolewa",
  h_cashout: "Kutoa pesa",
  h_forfeit: "Salio limepotea",
  co_PENDING: "Inasubiri",
  co_PROCESSING: "Inatumwa",
  co_COMPLETED: "Imetumwa",
  co_FAILED: "Imeshindikana, salio limerejeshwa",

  notifications: "Taarifa",
  noNotifications: "Bado hakuna kitu.",
  markAllRead: "Weka zote zimesomwa",

  profile: "Wasifu",
  lockedFields: "Jina, tarehe ya kuzaliwa na kitambulisho vinabadilishwa na Safiri pekee.",
  changePassword: "Badilisha nenosiri",
  currentPassword: "Nenosiri la sasa",
  passwordChanged: "Nenosiri limebadilishwa. Vifaa vingine vimetolewa.",
  changePhone: "Badilisha namba ya simu",
  changePhoneBody: "Tutatuma msimbo kwa namba yako ya sasa na kwa namba mpya. Umepoteza namba ya zamani? Wasiliana na Safiri.",
  newPhone: "Namba mpya ya simu",
  codeToOld: "Msimbo uliotumwa kwa namba ya sasa",
  codeToNew: "Msimbo uliotumwa kwa namba mpya",
  phoneChanged: "Namba ya simu imebadilishwa.",
  emailChangeSent: "Tumetuma kiungo kwa {email}. Barua pepe yako itabadilika ukikifungua.",
  deleteAccount: "Futa akaunti",
  deleteBody: "Msimbo wako utaacha kufanya kazi na rufaa zako zote zitafungwa. Hili haliwezi kutenduliwa, na huwezi kuomba tena kwa namba ile ile ya simu.",
  forfeitWarning: "Salio lako la {amount} ni chini ya kiwango cha chini cha kutoa na litapotea.",
  cashOutFirst: "Toa salio lako kabla ya kufuta akaunti.",
  delUnderReview: "Huwezi kufuta akaunti wakati maombi yako yanapitiwa.",
  delPayout: "Subiri malipo yako yakamilike kabla ya kufuta akaunti.",
  delNegative: "Salio la mkoba wako ni hasi. Wasiliana na Safiri kufuta akaunti.",
  confirmDelete: "Futa akaunti yangu",
  confirmWithPassword: "Weka nenosiri lako kuthibitisha",

  help: "Msaada",
  contactSupport: "Wasiliana na Safiri",
  terms: "Masharti na vigezo vya wakala",

  // Polish + PWA
  agentsLabel: "MAWAKALA",
  mainNav: "Menyu kuu",
  all: "Zote",
  add: "Ongeza",
  view: "Angalia",
  clear: "Futa",
  refresh: "Onyesha upya",
  choose: "Chagua…",
  next: "Mbele",
  previous: "Nyuma",
  pagination: "Kurasa",
  pageOf: "Ukurasa {page} kati ya {pages}",
  loadMore: "Onyesha zaidi",
  seeAll: "Ona zote",
  gotIt: "Nimeelewa",
  verified: "Imethibitishwa",
  notVerified: "Bado haijathibitishwa. Angalia barua pepe yako kwa kiungo.",
  unread: "Haijasomwa",
  showPassword: "Onyesha nenosiri",
  hidePassword: "Ficha nenosiri",
  steps: "Hatua",
  madeInTz: "Imetengenezwa kwa ajili ya Tanzania",
  goHome: "Rudi mwanzo",
  notFoundTitle: "Ukurasa haupatikani",
  notFoundBody: "Huenda kiungo ni cha zamani au kimeandikwa vibaya.",
  offlineBanner: "Huna mtandao. Unaona taarifa za mwisho ulizoziona.",
  updateReady: "Toleo jipya la programu liko tayari.",
  updateNow: "Sasisha",
  brandHeadline: "Leta madereva Smart Safiri. Lipwa kwa kila hatua.",
  brandPoint1: "Shiriki msimbo wako na madereva wapya",
  brandPoint2: "Wanajisajili, wanaidhinishwa na kuanza kuendesha",
  brandPoint3: "Zawadi zinaingia kwenye mkoba wako. Toa kwenda pesa kwa simu au benki",
  upToPerDriver: "Hadi TZS 10,000 kwa kila dereva mpya unayemleta.",
  signInTitle: "Karibu tena",
  signInBody: "Ingia kwa namba yako ya simu na nenosiri.",
  applyPitch: "Omba kwa dakika chache. Ukiidhinishwa unapata msimbo wako wa rufaa.",
  minCashoutNote: "Toa pesa kuanzia {amount}.",
  applyBody: "Fungua akaunti kwanza. Utaongeza taarifa zako na kitambulisho baadaye.",
  stepDetails: "Taarifa zako",
  stepVerifyPhone: "Thibitisha simu",
  fullNameHint: "Kama lilivyo kwenye kitambulisho chako.",
  phoneVerifyHint: "Tutatuma msimbo kwa WhatsApp (au SMS) kuthibitisha ni yako.",
  languageHint: "Inatumika kwa SMS na barua pepe kutoka Safiri.",
  errName: "Weka jina lako kamili.",
  errPhone: "Weka namba ya simu ya Tanzania.",
  errEmail: "Weka barua pepe sahihi.",
  pwRuleLength: "Herufi 8+",
  pwRuleLetter: "Herufi",
  pwRuleNumber: "Namba",
  editDetails: "Badilisha taarifa",
  resendIn: "Tuma tena baada ya sekunde {s}",
  verifyEmailFailed: "Hatukuweza kuthibitisha barua pepe yako",
  checkEmail: "Angalia barua pepe yako",
  backToSignIn: "Rudi kuingia",
  resetTitle: "Chagua nenosiri jipya",
  resetBody: "Utatolewa kwenye vifaa vyote.",
  confirmPassword: "Thibitisha nenosiri jipya",
  passwordsDiffer: "Manenosiri hayafanani.",
  passwordUpdated: "Nenosiri limebadilishwa",
  inviteInvalid: "Kiungo hiki cha mwaliko hakifanyi kazi",
  profileProgress: "Wasifu {done} kati ya {total} umekamilika",
  verifyYourEmail: "Thibitisha barua pepe yako",
  reviewTitle: "Asante! Tunakagua taarifa zako.",
  submittedOn: "Imetumwa {date}",
  dobHint: "Lazima uwe na miaka 18 au zaidi.",
  pickRegionFirst: "Chagua mkoa kwanza",
  tapToUpload: "Gusa kupakia",
  uploadDone: "Imepakiwa",
  openFile: "Fungua faili",
  fileTooBig: "Faili hilo ni zaidi ya MB 5. Chagua dogo zaidi.",
  referringStaffHint: "Kwa kumbukumbu zetu tu.",
  agreeAndSubmit: "Kubali na tuma",
  as_draft: "Rasimu",
  as_underReview: "Inakaguliwa",
  as_rejected: "Haikuidhinishwa",
  as_approved: "Wakala aliyeidhinishwa",
  as_suspended: "Imesimamishwa",
  suspendedTitle: "Akaunti imesimamishwa",
  shareCode: "Shiriki msimbo",
  shareTitle: "Jiunge na Smart Safiri kama dereva",
  codeCopied: "Msimbo umenakiliwa",
  messageCopied: "Ujumbe umenakiliwa",
  copyMessage: "Nakili ujumbe",
  codePageBody: "Ichapishe, ishiriki, au ionyeshe kwenye simu yako.",
  qrTitle: "Changanua kupata msimbo wangu",
  downloadQr: "Pakua",
  shareMessageTitle: "Ujumbe ulio tayari",
  howItWorks: "Inavyofanya kazi",
  how1: "Dereva anapakua programu ya madereva ya Smart Safiri.",
  how2: "Anaandika msimbo wako anapojisajili.",
  how3: "Unapata zawadi anapoidhinishwa na kukamilisha safari, ndani ya muda wa kila hatua.",
  driverPipeline: "Madereva wako",
  recentDrivers: "Madereva wa karibuni",
  firstDriverTitle: "Bado hakuna madereva",
  firstDriverBody: "Shiriki msimbo wako na mtu anayetaka kuendesha. Utamwona hapa akijisajili.",
  driversCount: "Madereva {n} uliowaleta",
  driverStatus: "Hali ya dereva",
  searchPlaceholder: "Jina au simu",
  noMatches: "Hakuna dereva anayelingana",
  noMatchesBody: "Jaribu jina lingine au ondoa vichujio.",
  clearFilters: "Ondoa vichujio",
  signedUpLabel: "Alijisajili",
  activatedLabel: "Aliidhinishwa",
  firstTripLabel: "Safari ya kwanza",
  lastTripLabel: "Safari ya mwisho",
  driverDeletedNote: "Dereva huyu alifuta akaunti yake. Zawadi ulizolipwa zinabaki kuwa zako.",
  stillToEarn: "Hadi {amount} zaidi hatua zinazofuata zikifikiwa kwa wakati.",
  milestones: "Hatua",
  toMinimum: "{amount} zaidi kufikia kiwango cha chini cha {min}",
  payoutAccounts: "Akaunti za malipo",
  payoutAccountsBody: "Mahali pesa zako zinapotumwa. Lazima ziwe kwa jina lako.",
  noAccounts: "Bado hakuna akaunti. Ongeza sasa au unapotoa pesa mara ya kwanza.",
  addAccountBody: "Pesa kwa simu au benki, kwa jina lako.",
  saveAccount: "Hifadhi akaunti",
  accountSaved: "Akaunti imehifadhiwa",
  removeAccount: "Ondoa",
  removeAccountTitle: "Ondoa akaunti hii?",
  removeAccountBody: "Unaweza kuiongeza tena baadaye. Malipo yaliyopita hayaathiriwi.",
  accountRemoved: "Akaunti imeondolewa",
  mobileMoney: "Pesa kwa simu",
  bank: "Benki",
  bankAccountNumber: "Namba ya akaunti ya benki",
  mobileNumberHint: "Namba ya simu ya mkoba, mfano 0712345678.",
  verifiedAccount: "Mmiliki wa akaunti amethibitishwa",
  nameMismatch: "Jina ni tofauti na lako",
  nameMismatchWarn: "Jina la akaunti hii halilingani na lako. Safiri inaweza kulikagua kabla ya malipo mengine.",
  chooseAccount: "Chagua mahali pa kutuma pesa zako.",
  useSavedAccount: "Tumia akaunti iliyohifadhiwa",
  confirmTitle: "Thibitisha kutoa pesa",
  youWillReceive: "Utapokea",
  noFee: "Hakuna ada ya kutuma: Safiri inalipa",
  filterRewards: "Zawadi",
  filterCashouts: "Malipo",
  filterOther: "Marekebisho",
  noHistoryBody: "Zawadi zitaonekana hapa madereva wako wanapofikia hatua.",
  receiptRef: "Kumb.",
  unreadCount: "{n} hazijasomwa",
  unreadNotifications: "Taarifa, {n} hazijasomwa",
  allRead: "Zote zimesomwa",
  noNotificationsBody: "Tutakujulisha madereva wanapojisajili, hatua zinapolipwa na pesa zinapofika.",
  pushTitle: "Arifa za papo hapo",
  pushOn: "Arifa zimewashwa",
  pushOnBody: "Utapata arifa kwenye kifaa hiki.",
  pushOffTitle: "Pata arifa papo hapo",
  pushOffBody: "Jua papo hapo dereva anapojisajili au unapolipwa.",
  pushDenied: "Arifa zimezuiwa kwa tovuti hii. Ziruhusu kwenye mipangilio ya kivinjari.",
  pushUnsupported: "Kivinjari hiki hakiwezi kuonyesha arifa. Bado utapata SMS na barua pepe.",
  pushUnavailable: "Arifa za papo hapo bado hazipatikani. Bado utapata SMS na barua pepe.",
  pushIosInstall: "Kwenye iPhone, weka programu kwenye skrini ya mwanzo ili kuwasha arifa.",
  pushTurnedOn: "Arifa zimewashwa",
  pushTurnedOff: "Arifa zimezimwa",
  turnOn: "Washa",
  turnOff: "Zima",
  install: "Sakinisha",
  installTitle: "Sakinisha programu ya wakala",
  installShort: "Fikia haraka kutoka skrini ya mwanzo",
  installBody: "Weka Smart Safiri Agents kwenye skrini ya mwanzo. Inafunguka kama programu na inafanya kazi hata mtandao ukiwa dhaifu.",
  iosStep1: "Gusa kitufe cha Shiriki kwenye Safari",
  iosStep2: "Chagua \"Add to Home Screen\"",
  iosStep3: "Gusa Add. Programu itaonekana kwenye skrini ya mwanzo",
  photoUpdated: "Picha ya wasifu imebadilishwa",
  agentSince: "Wakala tangu {date}",
  contactDetails: "Mawasiliano",
  editAfterReview: "Utaweza kubadilisha haya baada ya ombi lako kukaguliwa.",
  saveChanges: "Hifadhi mabadiliko",
  preferences: "Mapendeleo",
  appearance: "Mwonekano",
  themeSystem: "Otomatiki",
  themeLight: "Mwanga",
  themeDark: "Giza",
  themeToggle: "Mwonekano: {mode}. Gusa kubadilisha",
  security: "Usalama",
  changePasswordBody: "Vifaa vingine vitatolewa.",
  currentPhone: "Namba ya sasa",
  dangerZone: "Futa",
  deleteShort: "Funga akaunti yako ya wakala kabisa",
  deleteUnderstand: "Ninaelewa hili haliwezi kurudishwa.",
  goToWallet: "Nenda kwenye mkoba",
  helpBody: "Tuko hapa kukusaidia, kwa Kiingereza au Kiswahili.",
  callUs: "Tupigie",
  chatWithUs: "Ongea nasi",
  emailUs: "Tutumie barua pepe",
  faqTitle: "Maswali ya kawaida",
  faq1q: "Ninapataje zawadi?",
  faq1a: "Shiriki msimbo wako. Dereva mpya akijisajili nao, unapata zawadi kwa hatua: Safiri inapomwidhinisha, safari yake ya kwanza, na ya tano. Kila hatua ina muda wake, unaoonyeshwa kwenye ukurasa wa dereva.",
  faq2q: "Zawadi inalipwa lini?",
  faq2a: "Papo hapo, hatua inapofikiwa ndani ya muda wake. Inaonekana kwenye mkoba wako na unapata taarifa.",
  faq3q: "Kwa nini hatua imeisha muda?",
  faq3a: "Kila hatua lazima ifikiwe kabla ya saa 5:59 usiku siku yake ya mwisho. Ikikosekana, hatua hiyo tu inaisha; zinazofuata zinabaki wazi. Kumpigia dereva kumkumbusha kunasaidia.",
  faq4q: "Ninatoaje pesa?",
  faq4a: "Fungua Mkoba na ugusa Toa pesa salio likifikia {min}. Unatoa salio lote kwenda pesa kwa simu au akaunti ya benki kwa jina lako. Safiri inalipa ada.",
  faq5q: "Kwa nini siwezi kutoa pesa?",
  faq5a: "Kutoa pesa kunasitishwa malipo ya awali yakiwa bado njiani, salio likiwa chini ya kiwango au hasi, au akaunti ikiwa imesimamishwa. Ukurasa wa Mkoba unakuambia sababu.",
  faq6q: "Naweza kubadilisha jina au kitambulisho?",
  faq6a: "Jina, tarehe ya kuzaliwa na kitambulisho vinabadilishwa na huduma kwa wateja ya Safiri tu baada ya kuthibitisha utambulisho wako. Wasiliana nasi kwa njia zilizo juu.",
  faq7q: "Dereva anasema msimbo wangu haufanyi kazi",
  faq7a: "Hakikisha ameuandika sawasawa, herufi na namba. Misimbo inafanya kazi kwa madereva wapya tu: aliyewahi kuwa na akaunti ya dereva Safiri hawezi kuletwa. Akaunti yako ikisimamishwa, msimbo wako unasitishwa.",
  account: "Akaunti",
  support: "Msaada",
  profileShort: "Mawasiliano, nenosiri, lugha",
  helpShort: "Mawasiliano ya msaada na majibu",
  emailOptionalHint: "Si lazima. Tutatuma risiti na taarifa hapa ukiiweka.",
  forgotSmsBody: "Tutatuma msimbo kwenye namba ya simu ya akaunti yako, kwa WhatsApp au SMS.",
  forgotEmailBody: "Weka barua pepe ya akaunti yako tukutumie kiungo cha kubadilisha nenosiri.",
  useEmailInstead: "Tumia barua pepe yangu badala yake",
  useSmsInstead: "Pata msimbo kwa SMS badala yake",
  changeNumber: "Badilisha namba",
  verifyNotNeededTitle: "Hakuna haja ya kuthibitisha barua pepe",
  verifyNotNeededBody: "Mawakala hawahitaji tena kuthibitisha barua pepe. Unaweza kuendelea.",
  sendCodeWhatsApp: "Tuma msimbo kwa WhatsApp",
  sendCodeSms: "Tuma msimbo kwa SMS",
  useWhatsAppInstead: "Pata msimbo kwa WhatsApp badala yake",
  codeSentWhatsApp: "Tumetuma msimbo kwa {phone} kupitia WhatsApp.",
  sendBySmsInstead: "Tuma kwa SMS badala yake",
  sendOnWhatsAppInstead: "Tuma kwa WhatsApp badala yake",
  codesSentWhatsApp: "Tumetuma msimbo kwa WhatsApp kwa kila namba.",
  codesSentSms: "Tumetuma msimbo kwa SMS kwa kila namba.",
  usePhoneCodeInstead: "Tumia msimbo kwenye simu yangu badala yake",
};

export type TKey = Key;
const DICTS: Record<Lang, Record<Key, string>> = { en, sw };
const STORAGE_KEY = "safiri-agent-lang";

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "sw") return saved;
  } catch {
    // Storage blocked: fall through to the browser language.
  }
  return navigator.language?.toLowerCase().startsWith("sw") ? "sw" : "en";
}

interface I18n {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: Key, vars?: Record<string, string | number>) => string;
  /** Pick the right side of a backend { en, sw } message. */
  pick: (m: { en: string; sw: string } | string | null | undefined) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    document.documentElement.lang = l;
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // Not persisted; fine for this visit.
    }
  }, []);
  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang,
      t: (key, vars) => {
        let s = DICTS[lang][key] ?? en[key];
        if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
        return s;
      },
      pick: (m) => (!m ? "" : typeof m === "string" ? m : (m[lang] ?? m.en)),
    }),
    [lang, setLang]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n outside I18nProvider");
  return v;
}
